import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import QRCode from "qrcode";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Printer, X, Plus, Image as ImageIcon, Type, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { addDays, parseLocalDate, toIsoLocal } from "../travel/tripStore";
import { PrintShopPdfButton } from "@/components/PrintShopPdfButton";
import { elementToPdfBytes, elementToPngBlob } from "@/lib/printExport";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { ProjectBar } from "@/components/ProjectBar";
import { useExportGate } from "@/hooks/useExportGate";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { ExportUsageNote } from "@/components/ExportUsageNote";
import { useProject } from "@/lib/workflow/useProject";
import { eventSeed } from "@/lib/workflow/eventSeed";
import { makePassCode } from "@/lib/workflow/passCode";
import { runRowBatch, rowBatchTotals, zipRowResults } from "@/lib/workflow/rowBatch";
import { BADGE_WHO_LABEL, badgesFromGuests, type BadgeWho } from "../events/badgeData";

interface ContentBlock {
  id: number;
  type: "text" | "image";
  text?: string;
  imageUrl?: string;
}
let blockIdCounter = 1;
const nextBlockId = () => blockIdCounter++;

const OCCASIONS = {
  Birthday: {
    gradient: "linear-gradient(135deg, #F472B6, #FBBF24)",
    emoji: "🎉",
  },
  Wedding: {
    gradient: "linear-gradient(135deg, #E9D5FF, #FBCFE8)",
    emoji: "💍",
  },
  "Baby Shower": {
    gradient: "linear-gradient(135deg, #BAE6FD, #FBCFE8)",
    emoji: "🍼",
  },
  Anniversary: {
    gradient: "linear-gradient(135deg, #FCA5A5, #FDE68A)",
    emoji: "💕",
  },
  Graduation: {
    gradient: "linear-gradient(135deg, #93C5FD, #C4B5FD)",
    emoji: "🎓",
  },
  "Corporate Event": {
    gradient: "linear-gradient(135deg, #64748B, #1E293B)",
    emoji: "🏢",
  },
} as const;

type Occasion = keyof typeof OCCASIONS;

const TOOL_INFO_CONTENT = {
  what: `Creates invitations for birthdays, weddings, and other events, with six occasion presets each carrying their own color theme, ready to print or save as a PDF. Also generates a real calendar file (.ics) guests can add to their own calendar app for a reminder, and can make one personalised invitation per guest from your project, each with an optional QR pass, in a single ZIP.`,
  how: `Choose your occasion, fill in the event details (date, time, venue, RSVP info), and use your browser's print function to save the finished invitation. Set the structured event date/time to download a calendar file with a built-in reminder.`,
  tip: `Double and triple-check the date, time, and RSVP deadline before finalizing — these are the details most likely to cause real confusion if they're wrong, and the easiest to overlook when you're focused on how the invitation looks. The calendar file defaults to a 1-day-before reminder — once someone adds it to their own calendar app, they can change that lead time to whatever they prefer.`,
};

export default function InvitationCardMaker() {
  const [occasion, setOccasion] = useState<Occasion>("Birthday");
  const [headline, setHeadline] = useState("You're Invited!");
  const [hostName, setHostName] = useState("Sarah's 30th Birthday");
  const [message, setMessage] = useState(
    "Join us for an evening of celebration, food, and fun!"
  );
  // One structured date and time drives both the card text and the calendar file, so they can never disagree.
  const [eventDateISO, setEventDateISO] = useState(toIsoLocal(addDays(new Date(), 30)));
  const [eventTimeISO, setEventTimeISO] = useState("18:00");
  const dateObj = parseLocalDate(eventDateISO);
  const date = dateObj ? dateObj.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "";
  const time = (() => { const m = /^(\d{2}):(\d{2})$/.exec(eventTimeISO); if (!m) return ""; const h = +m[1]; return `${h % 12 || 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`; })();
  const [venue, setVenue] = useState("123 Main Street, Raleigh, NC");
  const [rsvp, setRsvp] = useState("RSVP by August 8 to sarah@example.com");
  const [phone, setPhone] = useState("(555) 123-4567");
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const printAreaRef = useRef<HTMLDivElement>(null);

  // Personalised batch: one invitation per guest from the shared project.
  const [project] = useProject();
  const { checkExport } = useExportGate();
  const { logUsageNow } = useLogUsageOnAction();
  // When a project is open (for example after Try a sample), start from its event instead of the birthday example.
  const seed = useMemo(() => eventSeed(project), [project]);
  const seedKey = seed ? JSON.stringify(seed) : "";
  const seededFor = useRef("");
  useEffect(() => {
    if (!seed || seededFor.current === seedKey) return;
    seededFor.current = seedKey;
    setOccasion("Corporate Event");
    setHostName(seed.title);
    setMessage(`Join us for ${seed.title}.`);
    setRsvp("Please reply to confirm you can attend.");
    setPhone("");
    if (seed.date) setEventDateISO(seed.date);
    if (seed.time) setEventTimeISO(seed.time);
    setVenue(seed.venue ?? "");
  }, [seed, seedKey]);
  const [personalise, setPersonalise] = useState(false);
  const [who, setWho] = useState<BadgeWho>("not-declined");
  const [greeting, setGreeting] = useState("Dear {name},");
  const [withQr, setWithQr] = useState(false);
  const [format, setFormat] = useState<"png" | "pdf">("pdf");
  const [current, setCurrent] = useState<{ name: string; qr: string | null } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const recipients = useMemo(() => badgesFromGuests(project.data.guests, who), [project.data.guests, who]);
  const MAX_INVITES = 300;
  const shown = current ?? (personalise && recipients[0] ? { name: recipients[0].name, qr: null } : null);
  const greetingLine = shown && greeting.trim() ? greeting.replace(/\{name\}/gi, shown.name) : "";

  const makeAll = async () => {
    const list = recipients.slice(0, MAX_INVITES);
    if (!list.length || !printAreaRef.current) { toast.error("No guests match. Add guests in the Guest List Tracker or open a project file."); return; }
    if (!(await checkExport())) return;
    const node = printAreaRef.current;
    setProgress(0);
    try {
      const jobs = await runRowBatch(list, async (g, i) => {
        const qr = withQr && g.id ? await QRCode.toDataURL(await makePassCode(project.passKey, g.id), { margin: 1, width: 240, errorCorrectionLevel: "M" }) : null;
        flushSync(() => setCurrent({ name: g.name, qr }));
        await new Promise((r) => requestAnimationFrame(() => r(null)));
        await new Promise((r) => setTimeout(r, qr ? 60 : 20)); // let the picture decode before capturing
        const base = `${String(i + 1).padStart(3, "0")}-${g.name.replace(/[\s\\/:*?"<>|.]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase().slice(0, 40) || "guest"}`;
        if (format === "png") return { blob: await elementToPngBlob(node), fileName: `${base}.png` };
        return { blob: new Blob([(await elementToPdfBytes(node)) as BlobPart], { type: "application/pdf" }), fileName: `${base}.pdf` };
      }, (js) => setProgress(Math.round((js.filter((j) => j.status === "done" || j.status === "failed").length / js.length) * 100)));
      const t = rowBatchTotals(jobs);
      downloadBlob(await zipRowResults(jobs, (g) => g.name), "invitations.zip");
      if (t.failed) toast.warning(`${t.done} made, ${t.failed} could not be made. not-made.txt in the ZIP lists them.`);
      else toast.success(`${t.done} invitation${t.done > 1 ? "s" : ""} in one ZIP${recipients.length > MAX_INVITES ? `. The first ${MAX_INVITES} were made; run the rest as a second batch.` : ""}`);
    } catch (err) {
      console.error("Invitation batch failed", err);
      toast.error("Couldn't build the invitations. Try fewer guests or the PNG format.");
    } finally { setCurrent(null); setProgress(null); }
  };

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBackgroundImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const addTextBlock = () =>
    setBlocks(prev => [...prev, { id: nextBlockId(), type: "text", text: "" }]);
  const addImageBlock = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBlocks(prev => [...prev, { id: nextBlockId(), type: "image", imageUrl: reader.result as string }]);
    };
    reader.readAsDataURL(file);
  };
  const updateBlockText = (id: number, text: string) =>
    setBlocks(prev => prev.map(b => (b.id === id ? { ...b, text } : b)));
  const removeBlock = (id: number) => setBlocks(prev => prev.filter(b => b.id !== id));

  const downloadAsImage = async () => {
    if (!printAreaRef.current) return;
    if (!(await checkExport())) return;
    try {
      const blob = await elementToPngBlob(printAreaRef.current); // ~300 DPI at 8.5 in wide
      downloadBlob(blob, `${hostName.replace(/\s+/g, "-").toLowerCase() || "invitation"}.png`);
      toast.success("Invitation downloaded as image!");
    } catch (err) {
      console.error("Failed to export invitation as image:", err);
      toast.error("Couldn't export the image. Try the Print / Save as PDF option instead.");
    }
  };

  const downloadAsPdf = async () => {
    if (!printAreaRef.current) return;
    if (!(await checkExport())) return;
    try {
      const bytes = await elementToPdfBytes(printAreaRef.current);
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), "invitation.pdf");
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("Couldn't build the PDF. Try the image download instead.");
    }
  };

  const downloadCalendarReminder = () => {
    if (!eventDateISO || !eventTimeISO) {
      toast.error("Set the event date and time first.");
      return;
    }
    const start = dateObj ? new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), +eventTimeISO.slice(0, 2), +eventTimeISO.slice(3, 5)) : new Date(NaN);
    if (isNaN(start.getTime())) {
      toast.error("That date/time couldn't be read — double-check the values.");
      return;
    }
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000); // default 2-hour block
    const toIcsDate = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"; // used for DTSTAMP only
    // Floating local time (no Z): "6:00 PM" on the card is 6:00 PM in the guest's calendar too, not shifted by a time zone.
    const pad = (n: number) => String(n).padStart(2, "0");
    const toLocalIcs = (d: Date) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
    const icsText = (t: string) => t.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
    // A real .ics file -- opening it lets someone add the event straight
    // to Google Calendar, Outlook, or Apple Calendar, where THEIR
    // calendar app handles the actual reminder notification at whatever
    // lead time they choose. This is a genuinely different (and more
    // reliable) mechanism than trying to schedule an SMS or email
    // reminder ourselves would be -- that would need real backend
    // infrastructure (a scheduled job plus an SMS/email delivery
    // service) this client-side tool doesn't have and isn't the right
    // place to build.
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//FreeDailyPro//Invitation Card Maker//EN",
      "BEGIN:VEVENT",
      `UID:${Date.now()}@freedailypro.com`,
      `DTSTAMP:${toIcsDate(new Date())}`,
      `DTSTART:${toLocalIcs(start)}`,
      `DTEND:${toLocalIcs(end)}`,
      `SUMMARY:${icsText(hostName || headline)}`,
      `DESCRIPTION:${icsText(message + (rsvp ? `\n\n${rsvp}` : ""))}`,
      `LOCATION:${icsText(venue)}`,
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder",
      "TRIGGER:-P1D", // default 1-day-before reminder; the person can change this once it's in their own calendar app
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    downloadBlob(new Blob([ics], { type: "text/calendar" }), `${(hostName || "event").replace(/\s+/g, "-").toLowerCase()}.ics`);
    toast.success("Calendar file downloaded — open it to add this event (with a reminder) to your calendar app.");
  };

  const theme = OCCASIONS[occasion];
  const isDark = occasion === "Corporate Event";

  return (
    <ToolContainer>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #invite-print-area, #invite-print-area * { visibility: visible; }
          #invite-print-area {
            position: absolute; top: 0; left: 0; width: 100%;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          @page { margin: 0.5in; }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4 print:hidden">
          <div>
            <Label className="mb-1 block text-sm">Occasion</Label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(OCCASIONS) as Occasion[]).map(o => (
                <Button
                  key={o}
                  size="sm"
                  variant={occasion === o ? "default" : "outline"}
                  onClick={() => setOccasion(o)}
                >
                  {OCCASIONS[o].emoji} {o}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Background Photo (optional)</Label>
            {backgroundImage ? (
              <div className="flex items-center gap-2">
                <img src={backgroundImage} alt="Selected background preview" className="h-12 w-20 object-cover rounded border" />
                <Button size="sm" variant="ghost" onClick={() => setBackgroundImage(null)}>
                  <X className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleImageUpload} label="Drop a photo, or keep the color theme above" />
            )}
          </div>
          <div>
            <Label className="mb-1 block text-sm">Headline</Label>
            <Input aria-label="Headline"
              value={headline}
              onChange={e => setHeadline(e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Event Title</Label>
            <Input aria-label="Event Title"
              value={hostName}
              onChange={e => setHostName(e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Message</Label>
            <Textarea aria-label="Message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="min-h-[70px]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-sm">Date</Label>
              <Input aria-label="Date" type="date" value={eventDateISO} onChange={e => setEventDateISO(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Time</Label>
              <Input aria-label="Time" type="time" value={eventTimeISO} onChange={e => setEventTimeISO(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Venue</Label>
            <Input aria-label="Venue" value={venue} onChange={e => setVenue(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">RSVP Info</Label>
            <Input aria-label="RSVP Info" value={rsvp} onChange={e => setRsvp(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Contact Phone (optional)</Label>
            <Input aria-label="Contact Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" />
          </div>
          <div className="p-3 rounded-lg border bg-muted/20">
            <p className="text-xs font-medium mb-2">Calendar reminder (optional)</p>
            <p className="text-xs text-muted-foreground mb-2">
              Uses the date and time above, so the file always matches the card. Guests can add it to Google
              Calendar, Outlook, or Apple Calendar — their calendar app handles the reminder.
            </p>
            <Button size="sm" variant="outline" onClick={downloadCalendarReminder} className="gap-1.5">
              <Download className="h-3.5 w-3.5" /> Download Calendar Reminder (.ics)
            </Button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm">Extra Content (optional)</Label>
            </div>
            <div className="space-y-2">
              {blocks.map(b => (
                <div key={b.id} className="flex gap-2">
                  {b.type === "text" ? (
                    <Textarea
                      value={b.text}
                      onChange={e => updateBlockText(b.id, e.target.value)}
                      placeholder="Directions, dress code, a note to guests..."
                      className="min-h-[50px]"
                    />
                  ) : (
                    <div className="flex-1 flex items-center gap-2 border rounded-md px-2 py-1">
                      <img src={b.imageUrl} alt="" className="h-8 w-12 object-cover rounded" />
                      <span className="text-xs text-muted-foreground">Image block</span>
                    </div>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => removeBlock(b.id)} className="shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={addTextBlock}>
                <Type className="h-3.5 w-3.5 mr-1" /> Add Text Block
              </Button>
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    addImageBlock(files);
                    e.target.value = "";
                  }}
                />
                <Button size="sm" variant="outline" asChild>
                  <span>
                    <ImageIcon className="h-3.5 w-3.5 mr-1" /> Add Image Block
                  </span>
                </Button>
              </label>
            </div>
          </div>
          <div className="p-3 rounded-lg border bg-muted/20 space-y-2" data-testid="inv-batch">
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={personalise} onChange={(e) => setPersonalise(e.target.checked)} data-testid="inv-personalise" /> Personalise for each guest in my project ({project.data.guests.length})</label>
            {personalise && (
              <div className="space-y-2">
                <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Which guests" value={who} onChange={(e) => setWho(e.target.value as BadgeWho)}>
                  {(Object.keys(BADGE_WHO_LABEL) as BadgeWho[]).map((k) => <option key={k} value={k}>{BADGE_WHO_LABEL[k]}</option>)}
                </select>
                <div><Label className="mb-1 block text-xs">Greeting line (use {"{name}"} for the guest's name; leave blank for none)</Label><Input value={greeting} onChange={(e) => setGreeting(e.target.value)} maxLength={80} data-testid="inv-greeting" /></div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={withQr} onChange={(e) => setWithQr(e.target.checked)} data-testid="inv-qr" /> Add each guest's QR pass (for the door check)</label>
                <div className="flex items-center gap-2 text-sm">
                  <Label className="text-xs">File type</Label>
                  <select className="border rounded px-2 py-1 bg-background text-sm" aria-label="File type" value={format} onChange={(e) => setFormat(e.target.value as "png" | "pdf")}><option value="pdf">PDF</option><option value="png">PNG image</option></select>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="inv-count">{recipients.length} invitation{recipients.length === 1 ? "" : "s"} will be made{recipients.length > MAX_INVITES ? ` (the first ${MAX_INVITES}; run the rest as a second batch)` : ""}. One use for the whole batch.</p>
                <Button className="w-full" onClick={makeAll} disabled={progress !== null || recipients.length === 0} data-testid="inv-make">{progress !== null ? `Making... ${progress}%` : "Make all invitations (ZIP)"}</Button>
              </div>
            )}
          </div>
          <Button className="w-full" onClick={async () => { if (await logUsageNow("download")) window.print(); }}>
            <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
          </Button>
          <Button className="w-full" variant="outline" onClick={downloadAsImage}>
            <Download className="h-4 w-4 mr-2" /> Download as Image
          </Button>
          <Button className="w-full" variant="outline" onClick={downloadAsPdf}>Download as PDF (Letter)</Button>
          <PrintShopPdfButton target={printAreaRef} filename="invitation-print-shop.pdf" defaultWidthIn={5} />
          <ExportUsageNote />
        </div>

        <div
          id="invite-print-area"
          ref={printAreaRef}
          className="rounded-2xl p-10 flex flex-col items-center justify-center text-center min-h-[500px] relative overflow-hidden self-start"
          style={{
            background: backgroundImage ? undefined : theme.gradient,
            color: backgroundImage ? "#fff" : isDark ? "#fff" : "#1f2937",
          }}
        >
          {backgroundImage && (
            <>
              <img src={backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
              {/* A photo's own colors can't be predicted, so a dark overlay keeps text legible regardless of what's in the photo. */}
              <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} />
            </>
          )}
          <div className="relative">
            <p className="text-4xl mb-4">{theme.emoji}</p>
            {greetingLine && <p className="text-lg mb-2" data-testid="inv-greeting-line">{greetingLine}</p>}
            <div className="text-3xl font-bold mb-2">{headline}</div>
            <h2 className="text-xl font-semibold mb-4">{hostName}</h2>
            <p className="mb-6 max-w-xs">{message}</p>
            <div className="space-y-1 font-medium">
              <p>{date}</p>
              <p>{time}</p>
              <p>{venue}</p>
            </div>
            <p className="mt-6 text-sm opacity-80">{rsvp}</p>
            {phone && <p className="text-sm opacity-80">{phone}</p>}
            {shown?.qr && <img src={shown.qr} alt="Guest pass QR code" className="mx-auto mt-4 h-24 w-24 rounded bg-white p-1" />}
            {blocks.length > 0 && (
              <div className="mt-6 space-y-3 max-w-xs">
                {blocks.map(b =>
                  b.type === "image" ? (
                    <img key={b.id} src={b.imageUrl} alt="" className="w-full rounded-lg object-cover max-h-40" />
                  ) : (
                    b.text && <p key={b.id} className="text-sm opacity-90">{b.text}</p>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ProjectBar />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
